// Reactive list of meet rooms keyed off root.rooms. Each row is a
// MeetRoomRow that navigates to /meet/:id on click. Live participant count
// (from root.liveParticipants) drives the "in progress" indicator.

// "Live" = has at least one meet_participants row with left_at IS NULL.
// The realtime subscription on meet_participants populates
// root.liveParticipants (keyed by roomId) across all rooms.
// `root` may be undefined during first-pass effect eval, so guard it.
const _isLive = (root, roomId) => {
  const p = root?.liveParticipants?.[roomId] || []
  return p.some((x) => !x.leftAt)
}

export const MeetRoomList = {
  flow: 'y',
  gap: 'V',
  childExtends: 'MeetRoomRow',
  childrenAs: 'state',
  children: (el, s) => {
    const rooms = s.root?.rooms || []
    // Live first, then not-ended, then ended. Within each group, most-recent
    // first. The _isLive check runs against realtime-populated state, so
    // rooms that just had their first join bubble up immediately.
    return rooms.slice().sort((a, b) => {
      const aLive = s.root ? _isLive(s.root, a.id) : false
      const bLive = s.root ? _isLive(s.root, b.id) : false
      if (aLive !== bLive) return aLive ? -1 : 1
      const aEnded = !!a.endedAt
      const bEnded = !!b.endedAt
      if (aEnded !== bEnded) return aEnded ? 1 : -1
      return (b.createdAt || '').localeCompare(a.createdAt || '')
    })
  }
}

export const MeetRoomRow = {
  flow: 'x',
  align: 'start',
  gap: 'Z1',
  padding: 'Z Z1',
  round: 'Z',
  cursor: 'pointer',
  background: (el, s) =>
    s.root?.activeRoomId === s.id ? 'overlay' : 'transparent',
  ':hover': { background: 'overlay' },

  onClick: (e, el, s) => {
    const id = s.id
    // Ended rooms go to the detail/list view — no call to connect.
    // Active rooms switch to call view and connect LiveKit imperatively
    // (page-level lifecycle hooks aren't reliable in DOMQL v3.14 for children
    // of if:/extends: wrappers, so the click handler drives the connect).
    if (s.endedAt) {
      s.root.update({ activeRoomId: id, meetView: 'list' })
      if (typeof window !== 'undefined')
        history.replaceState(null, '', `#room=${id}`)
      return
    }
    s.root.update({ activeRoomId: id, meetView: 'call' })
    if (typeof window !== 'undefined') {
      history.replaceState(null, '', `#room=${id}&view=call`)
    }
    el.call('connectCall', id)
  },

  LiveDot: {
    tag: 'span',
    boxSize: 'X2',
    minWidth: 'X2',
    minHeight: 'X2',
    round: '100%',
    margin: 'Z - -',
    background: (el, s) => {
      if (s.endedAt) return 'red'
      const live = (s.root?.liveParticipants?.[s.id] || []).filter(
        (p) => !p.leftAt
      )
      return live.length > 0 ? 'green' : 'gray'
    }
  },

  // Avatar stack of live participants (up to 5 + overflow count).
  // Reads from root.liveParticipants[roomId] filtered to unended entries,
  // then looks up each user_id in root.people for name/avatar.
  ParticipantStack: {
    flow: 'x',
    gap: 'V',
    align: 'center left',
    hide: (el, s) => {
      if (s.endedAt) return true
      const live = (s.root?.liveParticipants?.[s.id] || []).filter(
        (p) => !p.leftAt
      )
      return live.length === 0
    },

    AvatarList: {
      flow: 'x',
      gap: '-V', // slight overlap
      childrenAs: 'state',
      children: (el, s) => {
        const live = (s.root?.liveParticipants?.[s.id] || []).filter(
          (p) => !p.leftAt
        )
        const people = s.root?.people || []
        return live.slice(0, 5).map((lp) => {
          const person = people.find((pp) => pp.userId === lp.userId)
          return {
            id: lp.id,
            name: person?.name || lp.identity || '?',
            avatar: person?.avatar
          }
        })
      },
      childExtends: {
        width: 'B',
        height: 'B',
        round: '100%',
        borderWidth: '2px',
        borderColor: 'document',
        borderStyle: 'solid',
        background: 'gray',
        color: 'white',
        fontSize: 'Y2',
        fontWeight: '700',
        align: 'center center',
        text: (el, s) => {
          const name = s.name || ''
          return (
            name
              .split(/\s+/)
              .filter(Boolean)
              .slice(0, 2)
              .map((p) => p[0])
              .join('')
              .toUpperCase() || '?'
          )
        }
      }
    },

    OverflowCount: {
      hide: (el, s) => {
        const live = (s.root?.liveParticipants?.[s.id] || []).filter(
          (p) => !p.leftAt
        )
        return live.length <= 5
      },
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      text: (el, s) => {
        const live = (s.root?.liveParticipants?.[s.id] || []).filter(
          (p) => !p.leftAt
        )
        return `+${live.length - 5}`
      }
    }
  },

  Info: {
    flow: 'y',
    gap: 'V',
    flex: '1',
    minWidth: '0',
    overflow: 'hidden',

    Name: {
      tag: 'span',
      fontSize: 'Z2',
      color: 'title',
      fontWeight: '500',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => s.name || 'Untitled'
    },

    Caption: {
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => {
        if (s.endedAt) return 'Ended'
        const live = (s.root?.liveParticipants?.[s.id] || []).filter(
          (p) => !p.leftAt
        )
        if (live.length > 0) {
          return live.length === 1
            ? '1 person in call'
            : `${live.length} people in call`
        }
        if (s.transcribeEnabled) return 'Transcribed · idle'
        return s.kind || 'room'
      }
    }
  },

  // LIVE badge — only visible when there are connected participants right now.
  LiveBadge: {
    hide: (el, s) => {
      if (s.endedAt) return true
      const live = (s.root?.liveParticipants?.[s.id] || []).filter(
        (p) => !p.leftAt
      )
      return live.length === 0
    },
    tag: 'span',
    padding: 'V Z',
    round: 'Y',
    background: 'red',
    color: 'white',
    fontSize: 'Y1',
    fontWeight: '700',
    letterSpacing: '.05em',
    textTransform: 'uppercase',
    text: 'Live'
  },

  TranscribeBadge: {
    hide: (el, s) => !s.transcribeEnabled || s.endedAt,
    tag: 'span',
    fontSize: 'Y1',
    color: 'cyan',
    text: '●',
    title: 'Transcribed'
  },

  // Waiting badge — owner-only, shows when guests are waiting to join
  WaitingBadge: {
    hide: (el, s) => {
      const n = (s.root?.waitingRoom?.[s.id] || []).filter(
        (g) => !g.admittedAt && !g.rejectedAt
      ).length
      return n === 0
    },
    tag: 'span',
    padding: 'V Z',
    round: 'Y',
    background: 'orange',
    color: 'white',
    fontSize: 'Y1',
    fontWeight: '700',
    letterSpacing: '.05em',
    title: 'Guests waiting to join',
    text: (el, s) => {
      const n = (s.root?.waitingRoom?.[s.id] || []).filter(
        (g) => !g.admittedAt && !g.rejectedAt
      ).length
      return `${n} waiting`
    }
  }
}
